import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, PhoneOutgoing, MessageCircle, Calendar, FileText,
  CheckCircle, XCircle, Search, Filter, Plus, MoreHorizontal,
  Phone, Mail, MapPin, DollarSign, Tag, Clock, User, Building,
  ChevronRight, RefreshCw, ArrowRight, ExternalLink, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Stage icons mapping
const STAGE_ICONS = {
  new_lead: Sparkles,
  attempting_contact: PhoneOutgoing,
  contacted: MessageCircle,
  site_visit_scheduled: Calendar,
  estimate_sent: FileText,
  won: CheckCircle,
  lost: XCircle
};

// Source icons mapping
const SOURCE_ICONS = {
  google_ads: Search,
  facebook_ads: () => <span className="text-[10px] font-bold">f</span>,
  instagram_ads: () => <span className="text-[10px] font-bold">IG</span>,
  website_form: FileText,
  landing_page: FileText,
  phone_call: Phone,
  referral: User,
  direct: ArrowRight,
  other: MoreHorizontal
};

export default function CRMPipeline({ clientId = null }) {
  const [pipeline, setPipeline] = useState({});
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [draggedLead, setDraggedLead] = useState(null);
  const [filterSource, setFilterSource] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientFilter, setSelectedClientFilter] = useState(clientId || '');

  const fetchPipeline = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedClientFilter) params.append('client_id', selectedClientFilter);
      
      const res = await fetch(`${API}/api/crm/leads/pipeline?${params}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setPipeline(data.pipeline || {});
        setTotals(data.totals || {});
      }
    } catch (err) {
      console.error('Error fetching pipeline:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedClientFilter]);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/staff/clients`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  useEffect(() => {
    fetchPipeline();
    fetchClients();
  }, [fetchPipeline]);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedLead || draggedLead.pipeline_stage === targetStage) {
      setDraggedLead(null);
      return;
    }

    // If moving to lost, prompt for reason
    if (targetStage === 'lost') {
      const reason = window.prompt('Please enter a reason for marking this lead as lost:');
      if (!reason) {
        setDraggedLead(null);
        return;
      }
      
      await updateLeadStage(draggedLead.lead_id, targetStage, reason);
    } else {
      await updateLeadStage(draggedLead.lead_id, targetStage);
    }
    
    setDraggedLead(null);
  };

  const updateLeadStage = async (leadId, newStage, lostReason = null) => {
    try {
      const body = { pipeline_stage: newStage };
      if (lostReason) body.lost_reason = lostReason;

      const res = await fetch(`${API}/api/crm/leads/${leadId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (res.ok) {
        fetchPipeline();
      }
    } catch (err) {
      console.error('Error updating stage:', err);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const stageOrder = ['new_lead', 'attempting_contact', 'contacted', 'site_visit_scheduled', 'estimate_sent', 'won', 'lost'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Lead Pipeline</h1>
          <p className="text-slate-500 text-sm">Manage and track all leads through your sales process</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Lead
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm text-slate-500">Total Leads</p>
          <p className="text-2xl font-bold text-slate-800">{totals.total_leads || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm text-slate-500">Pipeline Value</p>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(totals.total_value)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm text-slate-500">Won</p>
          <p className="text-2xl font-bold text-green-600">{totals.won || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm text-slate-500">Lost</p>
          <p className="text-2xl font-bold text-red-500">{totals.lost || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm text-slate-500">Conversion</p>
          <p className="text-2xl font-bold text-blue-600">{totals.conversion_rate || 0}%</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm"
          />
        </div>
        
        {!clientId && (
          <select
            value={selectedClientFilter}
            onChange={(e) => setSelectedClientFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm"
          >
            <option value="">All Clients</option>
            {clients.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.business_name}</option>
            ))}
          </select>
        )}

        <button
          onClick={fetchPipeline}
          className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <RefreshCw className="w-5 h-5 text-slate-500" />
        </button>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
        {stageOrder.map((stageKey) => {
          const stage = pipeline[stageKey] || { leads: [], count: 0, total_value: 0, label: stageKey, color: '#6b7280' };
          const StageIcon = STAGE_ICONS[stageKey] || Sparkles;
          
          // Filter leads by search
          const filteredLeads = stage.leads.filter(lead => {
            if (!searchQuery) return true;
            const search = searchQuery.toLowerCase();
            return (
              lead.first_name?.toLowerCase().includes(search) ||
              lead.last_name?.toLowerCase().includes(search) ||
              lead.phone?.includes(search) ||
              lead.email?.toLowerCase().includes(search)
            );
          });

          return (
            <div
              key={stageKey}
              className="flex-shrink-0 w-72 bg-slate-50 rounded-2xl p-3"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stageKey)}
            >
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${stage.color}20` }}
                  >
                    <StageIcon className="w-4 h-4" style={{ color: stage.color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm">{stage.label}</h3>
                    <p className="text-xs text-slate-500">
                      {stage.count} leads • {formatCurrency(stage.total_value)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Lead Cards */}
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                <AnimatePresence>
                  {filteredLeads.map((lead) => {
                    const SourceIcon = SOURCE_ICONS[lead.source] || MoreHorizontal;
                    
                    return (
                      <motion.div
                        key={lead.lead_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead)}
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowLeadModal(true);
                        }}
                        className="bg-white border border-slate-200 rounded-xl p-3 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all group"
                      >
                        {/* Lead Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate">
                              {lead.first_name} {lead.last_name}
                            </p>
                            {lead.company && (
                              <p className="text-xs text-slate-500 truncate">{lead.company}</p>
                            )}
                          </div>
                          <div
                            className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${stage.color}15` }}
                            title={lead.source}
                          >
                            {typeof SourceIcon === 'function' ? (
                              <SourceIcon />
                            ) : (
                              <SourceIcon className="w-3 h-3" style={{ color: stage.color }} />
                            )}
                          </div>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center gap-2 text-xs text-slate-600">
                            <Phone className="w-3 h-3 text-slate-400" />
                            <span className="truncate">{lead.phone}</span>
                          </div>
                          {lead.email && (
                            <div className="flex items-center gap-2 text-xs text-slate-600">
                              <Mail className="w-3 h-3 text-slate-400" />
                              <span className="truncate">{lead.email}</span>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                          {lead.deal_value ? (
                            <span className="text-xs font-semibold text-green-600">
                              {formatCurrency(lead.deal_value)}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">No value</span>
                          )}
                          <span className="text-xs text-slate-400">{formatDate(lead.created_at)}</span>
                        </div>

                        {/* Service Type Tag */}
                        {lead.service_type && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-md">
                              {lead.service_type}
                            </span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {filteredLeads.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    No leads
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={showLeadModal}
        onClose={() => {
          setShowLeadModal(false);
          setSelectedLead(null);
        }}
        onUpdate={fetchPipeline}
      />

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          fetchPipeline();
        }}
        clients={clients}
        preselectedClientId={selectedClientFilter || clientId}
      />
    </div>
  );
}

// Lead Detail Modal Component
function LeadDetailModal({ lead, isOpen, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('details');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [fullLead, setFullLead] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lead && isOpen) {
      fetchLeadDetails();
    }
  }, [lead, isOpen]);

  const fetchLeadDetails = async () => {
    if (!lead) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/leads/${lead.lead_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setFullLead(data);
      }
    } catch (err) {
      console.error('Error fetching lead details:', err);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !lead) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/api/crm/leads/${lead.lead_id}/notes?note_text=${encodeURIComponent(newNote)}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setNewNote('');
        fetchLeadDetails();
      }
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setAddingNote(false);
    }
  };

  const sendToContractorOS = async () => {
    if (!lead) return;
    try {
      const res = await fetch(`${API}/api/crm/leads/${lead.lead_id}/send-to-contractor-os`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        alert('Lead marked for ContractorOS sync!');
        fetchLeadDetails();
        onUpdate();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  if (!isOpen) return null;

  const leadData = fullLead?.lead || lead;
  const callLogs = fullLead?.call_logs || [];
  const formSubmissions = fullLead?.form_submissions || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">
              {leadData?.first_name} {leadData?.last_name}
            </h2>
            {leadData?.company && (
              <p className="text-sm text-slate-500">{leadData.company}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!leadData?.contractor_os_synced && (
              <button
                onClick={sendToContractorOS}
                className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg flex items-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5" />
                Send to ContractorOS
              </button>
            )}
            {leadData?.contractor_os_synced && (
              <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                Synced to ContractorOS
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <XCircle className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-slate-200">
          <div className="flex gap-4">
            {['details', 'activity', 'calls', 'forms'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'calls' && callLogs.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                    {callLogs.length}
                  </span>
                )}
                {tab === 'forms' && formSubmissions.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                    {formSubmissions.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'details' && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Contact Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800">Contact Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-700">{leadData?.phone || '—'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-700">{leadData?.email || '—'}</span>
                      </div>
                      {leadData?.address && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                          <span className="text-slate-700">
                            {leadData.address}
                            {leadData.city && `, ${leadData.city}`}
                            {leadData.state && `, ${leadData.state}`}
                            {leadData.zip_code && ` ${leadData.zip_code}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Deal Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800">Deal Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-700">
                          {leadData?.deal_value 
                            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(leadData.deal_value)
                            : '—'}
                        </span>
                      </div>
                      {leadData?.service_type && (
                        <div className="flex items-center gap-3">
                          <Tag className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{leadData.service_type}</span>
                        </div>
                      )}
                      {leadData?.source && (
                        <div className="flex items-center gap-3">
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700 capitalize">{leadData.source.replace(/_/g, ' ')}</span>
                          {leadData.source_detail && (
                            <span className="text-slate-500">({leadData.source_detail})</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="col-span-2 space-y-4">
                    <h3 className="font-semibold text-slate-800">Notes</h3>
                    
                    {/* Add Note */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add a note..."
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && addNote()}
                      />
                      <button
                        onClick={addNote}
                        disabled={addingNote || !newNote.trim()}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-xl text-sm"
                      >
                        {addingNote ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add'}
                      </button>
                    </div>

                    {/* Notes List */}
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(leadData?.notes || []).map((note, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-xl p-3">
                          <p className="text-sm text-slate-700">{note.text}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {note.created_by_name || note.created_by} • {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                      {(!leadData?.notes || leadData.notes.length === 0) && (
                        <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="space-y-3">
                  {(leadData?.activities || []).map((activity, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-700">{activity.description}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!leadData?.activities || leadData.activities.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-8">No activity yet</p>
                  )}
                </div>
              )}

              {activeTab === 'calls' && (
                <div className="space-y-3">
                  {callLogs.length > 0 ? (
                    callLogs.map((call) => (
                      <div key={call.call_id} className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-500" />
                            <span className="font-medium text-slate-800">{call.caller_number}</span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            call.answered ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {call.answered ? 'Answered' : call.missed ? 'Missed' : 'Voicemail'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">Duration: {Math.floor(call.duration_seconds / 60)}m {call.duration_seconds % 60}s</p>
                        {call.recording_url && (
                          <audio controls className="mt-2 w-full">
                            <source src={call.recording_url} type="audio/mpeg" />
                          </audio>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(call.call_started_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No call recordings yet</p>
                      <p className="text-sm text-slate-400">Call logs will appear here once CallRail is connected</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'forms' && (
                <div className="space-y-3">
                  {formSubmissions.length > 0 ? (
                    formSubmissions.map((form) => (
                      <div key={form.submission_id} className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-slate-800">{form.form_name}</span>
                          <span className="text-xs text-slate-400">
                            {new Date(form.submitted_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(form.data || {}).map(([key, value]) => (
                            <div key={key}>
                              <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                              <p className="text-sm text-slate-700">{String(value)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No form submissions yet</p>
                      <p className="text-sm text-slate-400">Form submissions will appear here</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// Add Lead Modal Component
function AddLeadModal({ isOpen, onClose, onSuccess, clients, preselectedClientId }) {
  const [formData, setFormData] = useState({
    client_id: preselectedClientId || '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    company: '',
    source: 'other',
    deal_value: '',
    service_type: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preselectedClientId) {
      setFormData(prev => ({ ...prev, client_id: preselectedClientId }));
    }
  }, [preselectedClientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.first_name || !formData.phone) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/crm/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null
        })
      });

      if (res.ok) {
        onSuccess();
        setFormData({
          client_id: preselectedClientId || '',
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          company: '',
          source: 'other',
          deal_value: '',
          service_type: ''
        });
      }
    } catch (err) {
      console.error('Error creating lead:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800">Add New Lead</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
            >
              <option value="">Select client...</option>
              {clients.map(c => (
                <option key={c.client_id} value={c.client_id}>{c.business_name}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>

          {/* Source & Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
              >
                <option value="google_ads">Google Ads</option>
                <option value="facebook_ads">Facebook Ads</option>
                <option value="website_form">Website Form</option>
                <option value="phone_call">Phone Call</option>
                <option value="referral">Referral</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deal Value ($)</label>
              <input
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                placeholder="0"
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Type</label>
            <input
              type="text"
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              placeholder="e.g., Roof Repair, Kitchen Remodel"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !formData.client_id || !formData.first_name || !formData.phone}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-xl text-sm font-medium flex items-center gap-2"
            >
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Lead
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
